import { useState } from 'react'
import { 
  Play, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  XCircle,
  Cpu,
  HardDrive,
  MemoryStick,
  Wifi,
  Shield,
  Settings
} from 'lucide-react'

type DiagnosticStatus = 'idle' | 'running' | 'complete'
type ResultStatus = 'good' | 'warning' | 'critical' | 'pending'

interface DiagnosticItem {
  id: string
  name: string
  icon: typeof Cpu
  status: ResultStatus
  message?: string
}

const initialDiagnostics: DiagnosticItem[] = [
  { id: 'cpu', name: 'CPU Health', icon: Cpu, status: 'pending' },
  { id: 'memory', name: 'Memory Analysis', icon: MemoryStick, status: 'pending' },
  { id: 'disk', name: 'Disk Health', icon: HardDrive, status: 'pending' },
  { id: 'network', name: 'Network Connectivity', icon: Wifi, status: 'pending' },
  { id: 'security', name: 'Security Check', icon: Shield, status: 'pending' },
  { id: 'drivers', name: 'Driver Updates', icon: Settings, status: 'pending' },
]

const StatusIndicator = ({ status }: { status: ResultStatus }) => {
  switch (status) {
    case 'good':
      return <CheckCircle className="w-5 h-5 text-health-good" />
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-health-warning" />
    case 'critical':
      return <XCircle className="w-5 h-5 text-health-critical" />
    default:
      return <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
  }
}

export function DiagnosticsPage() {
  const [status, setStatus] = useState<DiagnosticStatus>('idle')
  const [diagnostics, setDiagnostics] = useState<DiagnosticItem[]>(initialDiagnostics)
  const [currentIndex, setCurrentIndex] = useState(0)

  const runDiagnostics = async () => {
    setStatus('running')
    setDiagnostics(initialDiagnostics)
    setCurrentIndex(0)

    // Simulate running each diagnostic
    for (let i = 0; i < initialDiagnostics.length; i++) {
      setCurrentIndex(i)
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Mock results - in real app, this would come from the API
      const mockResults: Record<string, { status: ResultStatus; message: string }> = {
        cpu: { status: 'good', message: 'CPU running at optimal temperature (45°C)' },
        memory: { status: 'good', message: '58% memory usage, no memory leaks detected' },
        disk: { status: 'warning', message: 'Storage at 78% capacity - consider cleanup' },
        network: { status: 'good', message: 'Connection stable, 12ms latency' },
        security: { status: 'good', message: 'No threats detected, definitions up to date' },
        drivers: { status: 'warning', message: '2 driver updates available' },
      }

      setDiagnostics(prev => 
        prev.map((item, idx) => 
          idx === i 
            ? { ...item, ...mockResults[item.id] }
            : item
        )
      )
    }

    setStatus('complete')
  }

  const getOverallStatus = () => {
    if (status !== 'complete') return null
    const hasWarning = diagnostics.some(d => d.status === 'warning')
    const hasCritical = diagnostics.some(d => d.status === 'critical')
    
    if (hasCritical) return { status: 'critical', message: 'Critical issues found!' }
    if (hasWarning) return { status: 'warning', message: 'Some issues need attention' }
    return { status: 'good', message: 'All systems healthy!' }
  }

  const overall = getOverallStatus()

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">System Diagnostics</h1>
        <p className="text-slate-400">
          Run a comprehensive scan to check your system health
        </p>
      </div>

      {/* Run Button */}
      <div className="mb-8">
        <button
          onClick={runDiagnostics}
          disabled={status === 'running'}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'running' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running Diagnostics...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              {status === 'complete' ? 'Run Again' : 'Start Diagnostic Scan'}
            </>
          )}
        </button>
      </div>

      {/* Overall Status */}
      {overall && (
        <div className={`card mb-6 border-2 ${
          overall.status === 'good' ? 'border-health-good/30 bg-health-good/10' :
          overall.status === 'warning' ? 'border-health-warning/30 bg-health-warning/10' :
          'border-health-critical/30 bg-health-critical/10'
        }`}>
          <div className="flex items-center gap-3">
            {overall.status === 'good' && <CheckCircle className="w-6 h-6 text-health-good" />}
            {overall.status === 'warning' && <AlertTriangle className="w-6 h-6 text-health-warning" />}
            {overall.status === 'critical' && <XCircle className="w-6 h-6 text-health-critical" />}
            <span className="text-lg font-medium text-white">{overall.message}</span>
          </div>
        </div>
      )}

      {/* Diagnostic Items */}
      <div className="space-y-4">
        {diagnostics.map((item, index) => {
          const Icon = item.icon
          const isRunning = status === 'running' && index === currentIndex
          const isComplete = item.status !== 'pending'

          return (
            <div 
              key={item.id}
              className={`card transition-all ${
                isRunning ? 'ring-2 ring-brand-primary' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isComplete ? 'bg-slate-700' : 'bg-slate-700/50'
                  }`}>
                    <Icon className={`w-5 h-5 ${isComplete ? 'text-slate-300' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{item.name}</h3>
                    {item.message && (
                      <p className={`text-sm ${
                        item.status === 'good' ? 'text-health-good' :
                        item.status === 'warning' ? 'text-health-warning' :
                        item.status === 'critical' ? 'text-health-critical' :
                        'text-slate-400'
                      }`}>
                        {item.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isRunning ? (
                    <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                  ) : (
                    <StatusIndicator status={item.status} />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
